// src/utils/buildVaultPath.js
//
// Single source of truth for FIGVault path construction per FIGVault_Spec_v1.6.
// Every code path that reads from or writes to the Clients/ branch of FIGVault
// must resolve its path through this helper (spec Part 4 item 4).
//
// Canonical path template (v1.3):
//   Clients/{CompanyName}/Policies/{LOB}/{Carrier}_{PolicyNum}_{DocType}_{EffectiveDate_ISO}.pdf
//
// Changes vs v1.2:
//   - Filename now requires an EffectiveDate_ISO suffix (YYYY-MM-DD). Missing or
//     unparseable EffectiveDate produces error INVALID_EFFECTIVE_DATE.
//   - DocType accepts Endorsement{NN} (e.g. Endorsement01) in addition to the
//     six-value enum.
//   - Exports statusToDocType(status) — canonical mapping per Part 4.3 Rule 9.
//     Callers MUST use this rather than hardcoding docType.
//   - EffectiveDate parsing delegates to parseLocalDate — never `new Date(str)`
//     (UTC parsing of ISO strings causes off-by-one in US timezones).

import { resolveCarrierShortname } from './carrierShortnameMap';
import { parseLocalDate } from './parseLocalDate';

const APPROVED_LOB_NAMES = new Set([
  'Commercial General Liability',
  'Commercial Property',
  'Commercial Auto',
  'Workers Compensation',
  'Excess Liability',
  'Umbrella',
  'Personal Umbrella',
  'Personal Auto',
  'Homeowners',
  'Errors and Omissions',
  'Employment Practices Liability',
  'Directors and Officers',
  'Inland Marine',
  'Cyber',
  'Equipment Breakdown',
  'Business Owners Policy',
  'Stock Throughput',
  'Foreign Liability',
  'Agency License',
]);

const BASE_DOC_TYPES = new Set(['Binder', 'Policy', 'Endorsement', 'Quote', 'Application', 'COI']);

function isValidDocType(dt) {
  if (typeof dt !== 'string' || !dt) return false;
  if (BASE_DOC_TYPES.has(dt)) return true;
  return /^Endorsement\d{2}$/.test(dt);
}

const BANNED_FS_CHARS = /[\\/:*?"<>|]/;
const BANNED_CONSTRUCTED_CHARS = /[&\\/:*?"<>|]/;

// Status → DocType mapping (spec v1.3 Part 4.3 Rule 9). Unknown/blank Status
// falls back to 'Policy' — safe default for legacy records whose Status field
// was never populated.
const STATUS_TO_DOC_TYPE = {
  Quoted: 'Quote',
  Bound: 'Binder',
  Active: 'Policy',
  Expired: 'Policy',
  Terminated: 'Policy',
};

export function statusToDocType(status) {
  if (!status) return 'Policy';
  return STATUS_TO_DOC_TYPE[String(status).trim()] || 'Policy';
}

export function stripPolicyNumber(raw) {
  let s = String(raw || '').trim();
  if (!s) return '';
  s = s.replace(/-0+$/, '');
  s = s.replace(/-/g, '');
  s = s.replace(/\s+/g, '');
  return s;
}

function formatISODate(dt) {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Build a spec-conformant FIGVault path for a policy document.
 *
 * @param {Object} args
 * @param {Object} args.insured          — FIG_Insureds record (must have CompanyName)
 * @param {Object} args.policy           — FIG_Policies record (must have LOB, PolicyNumber; Carrier/NAICCode optional)
 * @param {string} [args.docType]        — Binder | Policy | Endorsement | Endorsement{NN} | Quote | Application | COI. Default 'Policy'.
 * @param {string} [args.effectiveDate]  — YYYY-MM-DD or MM/DD/YYYY. Required when building filename (v1.3 R7).
 *                                         Defaults to policy.EffectiveDate if not supplied explicitly.
 * @param {Array}  [args.carriersList]   — Optional FIG_Carriers normalized array for NAIC consistency check
 * @returns {{
 *   folderPath: string|null,
 *   filename: string|null,
 *   fullPath: string|null,
 *   carrierShort: string|null,
 *   effectiveDateISO: string|null,
 *   warnings: Array<{code: string, message: string}>,
 *   errors: Array<{code: string, message: string}>
 * }}
 */
export function buildVaultPath({ insured, policy, docType = 'Policy', effectiveDate, carriersList } = {}) {
  const warnings = [];
  const errors = [];
  let folderPath = null;
  let filename = null;
  let carrierShort = null;
  let effectiveDateISO = null;

  const companyName = insured?.CompanyName ? String(insured.CompanyName).trim() : '';
  const lob = policy?.LOB ? String(policy.LOB).trim() : '';
  const policyNumber = policy?.PolicyNumber != null ? String(policy.PolicyNumber) : '';
  const carrierStr = policy?.Carrier ? String(policy.Carrier).trim() : '';
  const naicCode = policy?.NAICCode ? String(policy.NAICCode).trim() : '';
  const rawEffectiveDate = effectiveDate != null ? effectiveDate : policy?.EffectiveDate;

  if (!companyName) {
    errors.push({ code: 'MISSING_COMPANY_NAME', message: 'insured.CompanyName is required to construct a vault path.' });
  } else if (BANNED_FS_CHARS.test(companyName)) {
    errors.push({
      code: 'INVALID_COMPANY_NAME',
      message: `CompanyName "${companyName}" contains banned filesystem chars (/ \\ : * ? " < > |).`,
    });
  }

  if (!lob) {
    errors.push({ code: 'MISSING_LOB', message: 'policy.LOB is required to construct a vault path.' });
  } else if (BANNED_CONSTRUCTED_CHARS.test(lob)) {
    errors.push({
      code: 'INVALID_LOB',
      message: `LOB "${lob}" contains banned chars (& / \\ : * ? " < > |). LOB is constructed; Rule 4 wins over Rule 3.`,
    });
  } else if (!APPROVED_LOB_NAMES.has(lob)) {
    warnings.push({
      code: 'UNKNOWN_LOB',
      message: `LOB "${lob}" is not in the 19-value approved enum. Folder path constructed as-given.`,
    });
  }

  if (!isValidDocType(docType)) {
    errors.push({
      code: 'INVALID_DOC_TYPE',
      message: `docType "${docType}" is not in the enum (Binder | Policy | Endorsement | Endorsement{NN} | Quote | Application | COI).`,
    });
  }

  // EffectiveDate — required per v1.3 R7.
  if (rawEffectiveDate == null || String(rawEffectiveDate).trim() === '') {
    errors.push({
      code: 'MISSING_EFFECTIVE_DATE',
      message: 'EffectiveDate is required (v1.3 R7). Provide via args.effectiveDate or policy.EffectiveDate.',
    });
  } else {
    const parsed = parseLocalDate(rawEffectiveDate);
    if (!parsed) {
      errors.push({
        code: 'INVALID_EFFECTIVE_DATE',
        message: `EffectiveDate "${rawEffectiveDate}" could not be parsed (expected YYYY-MM-DD or MM/DD/YYYY).`,
      });
    } else {
      effectiveDateISO = formatISODate(parsed);
    }
  }

  const companyNameOk = companyName && !BANNED_FS_CHARS.test(companyName);
  const lobOk = lob && !BANNED_CONSTRUCTED_CHARS.test(lob);
  if (companyNameOk && lobOk) {
    folderPath = `Clients/${companyName}/Policies/${lob}`;
  }

  const policyNumberStripped = stripPolicyNumber(policyNumber);
  if (!policyNumberStripped) {
    errors.push({ code: 'MISSING_POLICY_NUMBER', message: 'policy.PolicyNumber is required to construct a filename.' });
  }

  if (!carrierStr && !naicCode) {
    errors.push({
      code: 'NO_CARRIER',
      message: 'policy.Carrier and policy.NAICCode both missing — cannot resolve carrier shortname.',
    });
  } else {
    const resolved = resolveCarrierShortname({ policyCarrier: carrierStr, policyNAIC: naicCode, carriersList });
    carrierShort = resolved.shortname;
    warnings.push(...resolved.warnings);
    if (!carrierShort) {
      errors.push({
        code: 'CARRIER_UNRESOLVABLE',
        message: `Could not resolve carrier shortname from NAIC "${naicCode}" or Carrier "${carrierStr}".`,
      });
    }
  }

  if (carrierShort && policyNumberStripped && isValidDocType(docType) && effectiveDateISO) {
    filename = `${carrierShort}_${policyNumberStripped}_${docType}_${effectiveDateISO}.pdf`;
  }

  const fullPath = folderPath && filename ? `${folderPath}/${filename}` : null;

  if (warnings.length > 0 && typeof console !== 'undefined') {
    for (const w of warnings) {
      console.warn('[buildVaultPath]', w.code, {
        insured: companyName,
        policy: policyNumber,
        message: w.message,
      });
    }
  }

  return { folderPath, filename, fullPath, carrierShort, effectiveDateISO, warnings, errors };
}

export default buildVaultPath;
